/**
 * Authentication Context for Project Adya
 * Provides auth state and methods throughout the application
 */

import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { apiClient } from '../services/apiClient';

interface User {
  id: string;
  email: string;
  full_name: string;
  role: string;
  is_active: boolean;
  created_at: string;
  last_login: string | null;
}

interface AuthContextType {
  user: User | null;
  effectiveRole: string | null;
  effectiveUserRole: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  setEffectiveRole: (role: string | null) => void;
  clearEffectiveRole: () => void;
  error: string | null;
  clearError: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [effectiveRole, setEffectiveRoleState] = useState<string | null>(null);

  // Load user from localStorage on mount
  useEffect(() => {
    const loadUser = () => {
      const storedUser = localStorage.getItem('user');
      const storedEffectiveRole = localStorage.getItem('effective_role');
      if (storedUser && apiClient.isAuthenticated()) {
        try {
          setUser(JSON.parse(storedUser));
        } catch (e) {
          localStorage.removeItem('user');
        }
      }

      if (storedEffectiveRole) {
        setEffectiveRoleState(storedEffectiveRole);
      }
      setIsLoading(false);
    };

    loadUser();
  }, []);

  useEffect(() => {
    const refreshMe = async () => {
      if (!apiClient.isAuthenticated()) return;
      try {
        const me = await apiClient.get<User>('/api/auth/me');
        setUser(me);
        localStorage.setItem('user', JSON.stringify(me));
      } catch {
        // apiClient handles 401 by clearing tokens + redirecting
      }
    };

    refreshMe();
  }, []);

  const login = async (email: string, password: string) => {
    try {
      setError(null);
      setIsLoading(true);
      const response = await apiClient.login(email, password);
      setUser(response.user);
    } catch (err: any) {
      setError(err.message || 'Login failed');
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const logout = async () => {
    try {
      await apiClient.logout();
    } finally {
      setUser(null);
      setEffectiveRoleState(null);
      // Redirect will happen from apiClient.clearTokens
    }
  };

  const setEffectiveRole = (role: string | null) => {
    apiClient.setEffectiveRole(role);
    setEffectiveRoleState(apiClient.getEffectiveRole());
  };

  const clearEffectiveRole = () => {
    apiClient.setEffectiveRole(null);
    setEffectiveRoleState(null);
  };

  const effectiveUserRole = user?.role === 'admin' ? (effectiveRole || user.role) : user?.role || null;

  const clearError = () => {
    setError(null);
  };

  const value: AuthContextType = {
    user,
    effectiveRole,
    effectiveUserRole,
    isAuthenticated: user !== null,
    isLoading,
    login,
    logout,
    setEffectiveRole,
    clearEffectiveRole,
    error,
    clearError,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
